import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import * as Hooks from '../hooks';
import * as Types from '../types';

const createSchema = z.object({
  testId: z.string().min(1, 'Test is required'),
  emails: z.array(z.string().email('Invalid email address')).min(1, 'At least one email is required'),
  expiresAt: z.date().optional(),
  message: z.string().optional(),
});

type FormValues = z.infer<typeof createSchema>;

interface IProps {
  testId?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  children: (props: {
    form: ReturnType<typeof useForm<FormValues>>;
    isSubmitting: boolean;
    handleSubmit: (e?: React.BaseSyntheticEvent) => void;
  }) => JSX.Element;
}

const Create: React.FC<IProps> = ({ testId, onSuccess, onError, children }) => {
  const mutation = Hooks.useCreate();

  const form = useForm<FormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      testId: testId || '',
      emails: [],
      expiresAt: undefined,
      message: '',
    },
  });

  const onSubmit = form.handleSubmit((values: FormValues) => {
    mutation.mutate(values as Types.IMutation.Create, {
      onSuccess: (data) => {
        form.reset();
        onSuccess?.(data);
      },
      onError: (error: any) => {
        onError?.(error?.response?.data?.message || 'Failed to send invitations');
      },
    });
  });

  return children({
    form,
    isSubmitting: mutation.isPending,
    handleSubmit: onSubmit,
  });
};

export default Create;
